import { getCurrentIdToken } from '../auth/AuthContext';

/**
 * Presigned URL取得APIのリクエスト型定義
 */
export interface GetPresignedUrlRequest {
  filename: string;
  file_size: number;
  content_type: string;
  media_type: 'image' | 'video';
}

/**
 * Presigned URL取得APIのレスポンス型定義（単一アップロード）
 */
export interface GetPresignedUrlResponseSingle {
  media_id: string;
  presigned_url: string;
  expires_in: number;
  upload_type: 'single';
  s3_key: string;
}

/**
 * Presigned URL取得APIのレスポンス型定義（マルチパートアップロード）
 */
export interface GetPresignedUrlResponseMultipart {
  media_id: string;
  upload_id: string;
  upload_type: 'multipart';
  part_size: number;
  total_parts: number;
  s3_key: string;
  part_urls: Array<{
    part_number: number;
    presigned_url: string;
  }>;
}

/**
 * Presigned URL取得APIのレスポンス型定義（ユニオン型）
 */
export type GetPresignedUrlResponse = GetPresignedUrlResponseSingle | GetPresignedUrlResponseMultipart;

/**
 * API GatewayのベースエンドポイントURLを取得
 * 環境変数 VITE_LAMBDA_ENDPOINT から取得
 * 
 * 設定例:
 * - 開発環境: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev
 * - 本番環境: https://<api-gateway-id>.execute-api.<region>.amazonaws.com/<stage>
 * 
 * 注意: 末尾にスラッシュは不要（コード内で追加されます）
 */
const getApiEndpoint = (): string => {
  // 環境変数からエンドポイントを取得（優先）
  const endpoint = import.meta.env.VITE_LAMBDA_ENDPOINT;
  if (endpoint) {
    // 末尾のスラッシュを削除して統一
    const cleanEndpoint = endpoint.endsWith('/') ? endpoint.slice(0, -1) : endpoint;
    return cleanEndpoint;
  }
  
  // 環境変数が設定されていない場合のフォールバック
  // 開発時のみ使用（本番環境では環境変数を必ず設定すること）
  // API Gatewayのエンドポイント（devステージ）
  return 'https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev';
};

/**
 * S3アップロード用のPresigned URLを取得する
 * 
 * @param request Presigned URL取得リクエスト
 * @returns Presigned URLとファイルURL
 */
export const getPresignedUrl = async (
  request: GetPresignedUrlRequest
): Promise<GetPresignedUrlResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/media/presigned-url
  const url = `${endpoint}/admin/media/presigned-url`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data: GetPresignedUrlResponse;
    if (responseData.body) {
      // bodyフィールドが存在する場合（Lambda関数の直接レスポンス形式）
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      // 直接JSONオブジェクトの場合（API Gateway経由の標準形式）
      data = responseData;
    }
    
    // バリデーション: upload_typeで型を判定
    if (data.upload_type === 'single' || data.upload_type === 'multipart') {
      return data;
    }
    
    throw new Error('無効なレスポンス形式です');
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。'
      );
    }
    
    throw error;
  }
};

/**
 * メタデータ保存APIのリクエスト型定義
 */
export interface SaveMediaMetadataRequest {
  media_id: string;
  s3_key: string;
  filename: string;
  file_size: number;
  content_type: string;
  media_type: 'image' | 'video';
}

/**
 * メタデータ保存APIのレスポンス型定義
 */
export interface SaveMediaMetadataResponse {
  media_id: string;
  is_duplicate: boolean;
  s3_url: string;
  cloudfront_url: string | null;
  uploaded_at: string;
}

/**
 * Presigned URLを使用してファイルをS3にアップロードする
 * 
 * @param presignedUrl Presigned URL
 * @param file アップロードするファイル
 * @param contentType Content-Type（Presigned URL生成時に指定したものと一致させる必要がある）
 * @returns アップロード成功時はtrue
 */
export const uploadFileToS3 = async (
  presignedUrl: string,
  file: File,
  contentType: string
): Promise<void> => {
  try {
    // Presigned URLにContent-Typeが条件として含まれている場合、
    // リクエスト時にも同じContent-Typeを指定する必要がある
    // Fileオブジェクトを直接bodyに設定すると、ブラウザが自動的にContent-Typeを設定する可能性があるため、
    // Blobに変換してから送信することで、Content-Typeを確実に制御する
    const blob = new Blob([file], { type: contentType });
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: blob,
    });
    
    if (!response.ok) {
      let errorMessage = `S3アップロードエラー: ${response.status} ${response.statusText}`;
      try {
        const errorText = await response.text();
        console.error('S3アップロードエラーレスポンス:', errorText);
        if (errorText) {
          errorMessage += `. ${errorText}`;
        }
      } catch {
        // エラーテキストの取得に失敗した場合は無視
      }
      
      // 403エラーの場合、より詳細なメッセージを提供
      if (response.status === 403) {
        errorMessage = `S3アップロードが拒否されました（403 Forbidden）。IAMロールの権限またはS3バケットポリシーを確認してください。指定したContent-Type: ${contentType}`;
      }
      
      throw new Error(errorMessage);
    }
  } catch (error) {
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error('S3へのアップロードに失敗しました。ネットワーク接続またはCORS設定を確認してください。');
    }
    throw error;
  }
};

/**
 * 画像ファイルをアップロードする（簡易版）
 * ファイル選択からメタデータ保存まで一括で処理
 * 
 * @param file アップロードするファイル
 * @param customFilename カスタムファイル名（オプション、拡張子は自動追加）
 * @param onProgress 進捗コールバック（オプション）
 * @returns アップロードされたファイルのURL
 */
export const uploadImageFile = async (
  file: File,
  customFilename?: string,
  onProgress?: (progress: number) => void
): Promise<string> => {
  // メディアタイプを判定
  const getMediaType = (contentType: string): 'image' | 'video' | null => {
    if (contentType.startsWith('image/')) {
      return 'image';
    } else if (contentType.startsWith('video/')) {
      return 'video';
    }
    return null;
  };

  // ファイル名から拡張子を取得
  const getFileExtension = (fileName: string): string => {
    const lastDot = fileName.lastIndexOf('.');
    return lastDot >= 0 ? fileName.substring(lastDot) : '';
  };

  // メディアタイプを判定
  const mediaType = getMediaType(file.type);
  if (!mediaType || mediaType !== 'image') {
    throw new Error('画像ファイルのみアップロード可能です。');
  }

  // ファイルサイズチェック（画像は5MB制限）
  const maxSizeImage = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSizeImage) {
    throw new Error('画像は5MB以下を選択してください。');
  }

  // ファイル名を確定
  const originalExtension = getFileExtension(file.name);
  const finalFilename = customFilename
    ? (customFilename.includes('.') ? customFilename : customFilename + originalExtension)
    : file.name;

  onProgress?.(10);

  // 1. Presigned URLを取得
  const contentType = file.type || 'application/octet-stream';
  const request: GetPresignedUrlRequest = {
    filename: finalFilename,
    file_size: file.size,
    content_type: contentType,
    media_type: 'image',
  };

  const presignedResponse = await getPresignedUrl(request);
  onProgress?.(30);

  // 2. S3にファイルをアップロード
  if (presignedResponse.upload_type === 'single') {
    await uploadFileToS3(
      presignedResponse.presigned_url,
      file,
      contentType
    );
    onProgress?.(70);
  } else {
    throw new Error('マルチパートアップロードは現在サポートされていません。');
  }

  // 3. メタデータを保存
  const metadataRequest: SaveMediaMetadataRequest = {
    media_id: presignedResponse.media_id,
    s3_key: presignedResponse.s3_key,
    filename: finalFilename,
    file_size: file.size,
    content_type: contentType,
    media_type: 'image',
  };

  const metadataResponse = await saveMediaMetadata(metadataRequest);
  onProgress?.(100);

  // CloudFront URLが存在する場合はそれを使用、なければS3 URLを使用
  return metadataResponse.cloudfront_url || metadataResponse.s3_url;
};

/**
 * メディアメタデータを保存する
 * 
 * @param request メタデータ保存リクエスト
 * @returns メタデータ保存レスポンス
 */
export const saveMediaMetadata = async (
  request: SaveMediaMetadataRequest
): Promise<SaveMediaMetadataResponse> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/media/metadata
  const url = `${endpoint}/admin/media/metadata`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 404) {
        let errorMessage = 'S3にファイルが存在しません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data: SaveMediaMetadataResponse;
    if (responseData.body) {
      // bodyフィールドが存在する場合（Lambda関数の直接レスポンス形式）
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      // 直接JSONオブジェクトの場合（API Gateway経由の標準形式）
      data = responseData;
    }
    
    return data;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。'
      );
    }
    
    throw error;
  }
};

/**
 * メディア一覧取得APIのリクエストパラメータ型定義
 */
export interface GetMediaListParams {
  page?: number;
  per_page?: number;
  media_type?: 'image' | 'video';
  search?: string;
}

/**
 * メディア情報の型定義
 */
export interface MediaItem {
  media_id: string;
  media_type: 'image' | 'video';
  original_filename: string;
  file_size: number;
  content_type: string;
  s3_url: string;
  cloudfront_url: string | null;
  uploaded_at: string;
  uploaded_by: string;
  used_in_posts: string[];
  used_count: number;
}

/**
 * メディア一覧取得APIのメタ情報型定義
 */
export interface MediaListMeta {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
  media_type: string | null;
  search: string | null;
}

/**
 * メディア一覧取得APIのレスポンス型定義
 */
export interface GetMediaListResponse {
  media: MediaItem[];
  meta: MediaListMeta;
}

/**
 * メディア詳細を取得する
 * 
 * @param mediaId メディアID
 * @returns メディア詳細情報
 */
export const getMedia = async (mediaId: string): Promise<MediaItem> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/media/{media_id}
  const url = `${endpoint}/admin/media/${encodeURIComponent(mediaId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        let errorMessage = '指定されたメディアが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // レスポンスボディをパース
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data: MediaItem;
    if (responseData.body) {
      // bodyフィールドが存在する場合（Lambda関数の直接レスポンス形式）
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      // 直接JSONオブジェクトの場合（API Gateway経由の標準形式）
      data = responseData;
    }
    
    return data;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。'
      );
    }
    
    throw error;
  }
};

/**
 * メディア一覧を取得する
 * 
 * @param params クエリパラメータ
 * @returns メディア一覧とメタ情報
 */
export const getMediaList = async (params: GetMediaListParams = {}): Promise<GetMediaListResponse> => {
  const { page = 1, per_page = 20, media_type, search } = params;
  
  // クエリパラメータを構築
  const queryParams = new URLSearchParams();
  queryParams.append('page', page.toString());
  queryParams.append('per_page', per_page.toString());
  if (media_type) {
    queryParams.append('media_type', media_type);
  }
  if (search && search.trim()) {
    queryParams.append('search', search.trim());
  }
  
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/media?page=1&per_page=20
  const url = `${endpoint}/admin/media?${queryParams.toString()}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status === 404) {
        throw new Error('メディア一覧取得APIが見つかりません。API Gatewayのエンドポイント設定を確認してください。');
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = `サーバーエラー: ${errorData.message}`;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクトとして返される
    const responseData = await response.json();
    
    // API Gateway経由の場合、レスポンスは直接JSONオブジェクト
    // ただし、Lambda関数の実装によってはbodyフィールドに含まれる可能性があるため、
    // 両方の形式に対応
    let data: GetMediaListResponse;
    if (responseData.body) {
      // bodyフィールドが存在する場合（Lambda関数の直接レスポンス形式）
      data = typeof responseData.body === 'string' 
        ? JSON.parse(responseData.body) 
        : responseData.body;
    } else {
      // 直接JSONオブジェクトの場合（API Gateway経由の標準形式）
      data = responseData;
    }
    
    return data;
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。'
      );
    }
    
    throw error;
  }
};

/**
 * メディアを削除する
 * 
 * @param mediaId 削除するメディアのID
 * @returns 削除成功時は何も返さない
 */
export const deleteMedia = async (mediaId: string): Promise<void> => {
  const endpoint = getApiEndpoint();
  // 管理画面用のエンドポイント
  // 例: https://5iu8q9zvu1.execute-api.us-east-1.amazonaws.com/dev/admin/media/{media_id}
  const url = `${endpoint}/admin/media/${encodeURIComponent(mediaId)}`;
  
  try {
    const idToken = await getCurrentIdToken();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers,
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      
      // デバッグ用: エラーレスポンスをログ出力
      console.error('Delete Media API Error:', {
        status: response.status,
        statusText: response.statusText,
        url,
        errorText,
      });
      
      // ステータスコードに応じたエラーメッセージを提供
      if (response.status === 400) {
        let errorMessage = 'リクエストが不正です';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 404) {
        let errorMessage = '指定されたメディアが見つかりません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 409) {
        // 使用中のメディアは削除不可
        let errorMessage = 'このメディアは使用中のため削除できません';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status === 403) {
        let errorMessage = 'APIへのアクセスが拒否されました';
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message === 'Missing Authentication Token') {
            errorMessage = '認証トークンが不足しています。API Gatewayの設定を確認してください。';
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        const error = new Error(errorMessage);
        (error as any).isAuthError = true;
        throw error;
      } else if (response.status >= 400 && response.status < 500) {
        // クライアントエラーの場合（400-499）
        let errorMessage = `リクエストエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      } else if (response.status >= 500) {
        // サーバーエラーの場合（500-599）
        let errorMessage = `サーバーエラー (${response.status})`;
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.message) {
            // サーバー側から詳細なエラーメッセージが返ってきた場合は、それをそのまま使用
            errorMessage = errorData.message;
          }
        } catch {
          // JSONパースに失敗した場合はデフォルトメッセージを使用
        }
        throw new Error(errorMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // 削除成功時はレスポンスボディがない可能性があるため、何も返さない
  } catch (error) {
    // CORSエラーの場合、より分かりやすいエラーメッセージを提供
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw new Error(
        'CORSエラー: API GatewayのCORS設定を確認してください。開発環境では、API GatewayでCORSを有効化する必要があります。'
      );
    }
    
    throw error;
  }
};
